/** Forward analyst estimate horizon exposed in the UI and valuation indicators. */
export const FORWARD_ESTIMATE_QUARTERS = 8;

/** Next-twelve-months window used by Forward P/E and forward CAGR indicators. */
export const NTM_ESTIMATE_QUARTERS = 4;

function parseEstimateDate(row) {
  const raw = row?.fiscalDateEnding ?? row?.date ?? null;
  if (!raw) return null;
  const s = String(raw).slice(0, 10);
  const d = new Date(`${s}T00:00:00Z`);
  return Number.isFinite(d.getTime()) ? d : null;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Build forward EPS or revenue rows from raw FMP `/analyst-estimates` rows. */
export function forwardRowsFromAnalystEstimates(analystEstimates, type) {
  if (!Array.isArray(analystEstimates) || !analystEstimates.length) return [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const out = [];

  for (const row of analystEstimates) {
    const d = parseEstimateDate(row);
    if (!d || d < today) continue;

    const estimate =
      type === "eps"
        ? num(
            row.estimatedEpsAvg ??
              row.estimatedEPSAvg ??
              row.epsEstimate ??
              row.estimatedEps ??
              row.estimate
          )
        : num(
            row.estimatedRevenueAvg ??
              row.revenueEstimate ??
              row.estimatedRevenue ??
              row.estimate
          );

    if (estimate == null || estimate <= 0) continue;
    const date = d.toISOString().slice(0, 10);
    out.push({ date, estimate, _t: d.getTime() });
  }

  out.sort((a, b) => a._t - b._t);
  const seen = new Set();
  const deduped = [];
  for (const row of out) {
    if (seen.has(row.date)) continue;
    seen.add(row.date);
    deduped.push({ date: row.date, estimate: row.estimate });
    if (deduped.length >= FORWARD_ESTIMATE_QUARTERS) break;
  }
  return deduped;
}

function pickForwardRows(summaryBox, analystEstimates, type) {
  const fromApi = forwardEstimateRows(summaryBox);
  if (fromApi.length > 0) {
    const apiDates = new Set(fromApi.map((r) => r.date || r.fiscalDateEnding).filter(Boolean));
    const extra = forwardRowsFromAnalystEstimates(analystEstimates, type).filter(
      (r) => r.date && !apiDates.has(r.date)
    );
    return [...fromApi, ...extra].slice(0, FORWARD_ESTIMATE_QUARTERS);
  }
  return forwardRowsFromAnalystEstimates(analystEstimates, type).slice(0, FORWARD_ESTIMATE_QUARTERS);
}

/** Merge `/analyst-forecasts` summary with paginated `fundamentals.analystEstimates` when needed. */
export function enrichAnalystForecasts(forecasts, analystEstimates = null) {
  if (!forecasts && !analystEstimates?.length) return null;
  const base = forecasts || { eps: {}, revenue: {} };
  return {
    eps: {
      next8: pickForwardRows(base.eps, analystEstimates, "eps"),
      accuracy5yPct: base.eps?.accuracy5yPct ?? base.eps?.accuracy ?? null,
    },
    revenue: {
      next8: pickForwardRows(base.revenue, analystEstimates, "revenue"),
      accuracy5yPct: base.revenue?.accuracy5yPct ?? base.revenue?.accuracy ?? null,
    },
  };
}

/** Up to eight forward quarterly estimate rows from an EPS or revenue forecast box. */
export function forwardEstimateRows(metricBox) {
  if (!metricBox) return [];
  const rows = metricBox.next8 ?? metricBox.next4 ?? [];
  return Array.isArray(rows) ? rows.slice(0, FORWARD_ESTIMATE_QUARTERS) : [];
}

/** First four forward quarters for NTM-based indicator math. */
export function ntmEstimateRows(metricBox) {
  return forwardEstimateRows(metricBox).slice(0, NTM_ESTIMATE_QUARTERS);
}

/** Normalize one forecast metric box from the API. */
export function normalizeForecastMetricBox(rawBox) {
  const norm = (arr) =>
    (arr || []).map((x) => ({
      date: x.fiscalDateEnding || x.date || null,
      estimate: x.estimate,
    }));
  const forward = norm(rawBox?.next8 ?? rawBox?.next4).slice(0, FORWARD_ESTIMATE_QUARTERS);
  return {
    next8: forward,
    accuracy5yPct: rawBox?.accuracy5yPct ?? rawBox?.accuracy ?? null,
  };
}
